import React, { useCallback, useState, useEffect } from 'react';

import { Skeleton } from '@material-ui/lab';
import { useDispatch, useSelector } from 'react-redux';
import { resetView } from 'reducers/viewsSlice';
import styled from 'styled-components';
import { Divider1, StyledGrid, StyledPaper, VerticallyAlignedFlexContainer } from 'styles/common';

import { createCustomDefinition, getCustomDefinitionDetails, updateCustomDefinition } from 'services/customDefinition';
import { showErrorSnackbar } from 'services/snackbar';

import { ButtonFilled, ButtonOutlined } from 'uiToolkit/Button';
import { Typography13 } from 'uiToolkit/Typography';

import { CUSTOM_ROLE_ACTION_TYPES, useCustomRoleContext } from '../CustomRoleContext';
import AttributesSection from './AttributesSection';
import CustomRoleName from './CustomRoleName';
import DataAccessSection from './DataAccessSection';
import { resetEmployeesFilters, selectEmployeesFilters, setEmployeesFilters } from './createCustomRoleSlice';

const FooterContainer = styled(VerticallyAlignedFlexContainer)`
  padding: 1rem 0;
  justify-content: flex-end;
`;

const Loader = () => (
  <StyledGrid container padding="24px" direction="column" alignItems="center">
    <StyledGrid item xs="6" style={{ width: '100%', alignSelf: 'flex-start' }}>
      <Skeleton variant="rect" width="100%" height="1.5rem" style={{ marginBottom: '2rem' }} />
    </StyledGrid>

    <StyledGrid container style={{ width: '100%', marginBottom: '2rem' }}>
      <StyledGrid item padding="8px 8px 8px 0px" xs="5" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="100%" height="1.5rem" />
      </StyledGrid>
      <StyledGrid item padding="8px 0px 8px 8px" xs="5" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="100%" height="1.5rem" />
      </StyledGrid>
    </StyledGrid>

    <StyledGrid container justify="space-around" style={{ width: '100%' }}>
      <StyledGrid item padding="8px 8px 8px 0px" xs="12" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="40%" height="1rem" />
      </StyledGrid>
      <StyledGrid item padding="8px 8px 8px 0px" xs="12" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="40%" height="1rem" />
      </StyledGrid>
      <StyledGrid item padding="8px 8px 8px 0px" xs="12" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="40%" height="1rem" />
      </StyledGrid>
      <StyledGrid item padding="8px 8px 8px 0px" xs="12" style={{ width: '100%' }}>
        <Skeleton variant="rect" width="40%" height="1rem" />
      </StyledGrid>
    </StyledGrid>
  </StyledGrid>
);

const CreateCustomRoleForm = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState('');

  const [permissions, setPermissions] = useState([]);
  const employeesFilters = useSelector(selectEmployeesFilters);

  const {
    state: { editingRoleId },
    customRoleDispatch,
  } = useCustomRoleContext();

  const handleNameChange = useCallback((ev) => {
    ev.stopPropagation();
    setName(ev.target.value);
  }, []);

  const handlePermissionChange = useCallback((permission) => {
    setPermissions((previousPermissions) =>
      previousPermissions.includes(permission)
        ? previousPermissions.filter((p) => p !== permission)
        : [...previousPermissions, permission]
    );
  }, []);

  const isValidCustomRole = () => {
    if (name.trim().length === 0) return false;
    if (Object.keys(employeesFilters || {}).length === 0) return false;
    if (permissions.length === 0) return false;
    return true;
  };

  const resetAccessView = useCallback(() => {
    dispatch(resetView());
    dispatch(resetEmployeesFilters());
  }, [dispatch]);

  const saveCustomRoleDefinition = () => {
    // do basic validation and make api call.

    const roleObj = {
      name,
      cohortQuery: {
        ...employeesFilters,
      },
      permissionTypes: permissions,
    };

    if (editingRoleId) {
      // update data
      updateCustomDefinition({ id: editingRoleId, customDefinitionObj: roleObj })
        .then((res) => {
          console.log(res);
          customRoleDispatch({
            type: CUSTOM_ROLE_ACTION_TYPES.updateRole,
            payload: {
              id: editingRoleId,
              name: roleObj.name,
            },
          });
        })
        .catch((err) => {
          console.error(err);
          dispatch(showErrorSnackbar(err.message || 'failed to update custom definition'));
        });
    } else {
      createCustomDefinition(roleObj)
        .then((res) => {
          console.log(res);
          customRoleDispatch({
            type: CUSTOM_ROLE_ACTION_TYPES.createNewRole,
            payload: {
              id: res.id,
              name: roleObj.name,
            },
          });
        })
        .catch((err) => {
          console.error(err);
          dispatch(showErrorSnackbar(err.message || 'failed to create custom definition'));
        })
        .finally(() => {
          resetAccessView();
        });
    }
  };

  const handleCancel = useCallback(() => {
    customRoleDispatch({
      type: CUSTOM_ROLE_ACTION_TYPES.openDefaultView,
      payload: null,
    });
    resetAccessView();
  }, [customRoleDispatch, resetAccessView]);

  const fetchCustomDefinitionDetails = useCallback(
    (id) => {
      setLoading(true);
      getCustomDefinitionDetails({ id })
        .then((res) => {
          setName(res?.name);
          const newEmployeeFilters = Object.fromEntries(
            Object.entries(res?.cohortQuery || {}).filter(
              (curQuery) => curQuery?.[1] !== null && curQuery?.[1] !== undefined && curQuery?.[1].length > 0
            )
          );
          dispatch(setEmployeesFilters({ employeesFilters: newEmployeeFilters }));
          setPermissions(res?.permissionTypes);
          setLoading(false);
        })
        .catch((err) => {
          console.log(err);
          dispatch(showErrorSnackbar(err.message || 'Failed to fetch definition details'));
        });
    },
    [dispatch]
  );

  useEffect(
    () => () => {
      resetAccessView();
    },
    [resetAccessView]
  );

  useEffect(() => {
    if (editingRoleId) fetchCustomDefinitionDetails(editingRoleId);
  }, [editingRoleId]);

  return (
    <div>
      {loading ? (
        <StyledPaper>
          <Loader />
        </StyledPaper>
      ) : (
        <>
          <StyledPaper>
            <CustomRoleName name={name} handleNameChange={handleNameChange} />
            <Divider1 />
            <AttributesSection employeeFilters={employeesFilters} />
            <Divider1 />
            <DataAccessSection selectedPermissions={permissions} handlePermissionChange={handlePermissionChange} />
          </StyledPaper>
          <FooterContainer>
            <ButtonOutlined
              width="160px"
              borderColor="primary"
              fontColor="primary"
              customColor="white"
              onClick={handleCancel}
              rightGutter={3}
            >
              <Typography13>Cancel </Typography13>
            </ButtonOutlined>
            <ButtonFilled width="160px" onClick={saveCustomRoleDefinition} disabled={!isValidCustomRole()}>
              <Typography13>Save </Typography13>
            </ButtonFilled>
          </FooterContainer>
        </>
      )}
    </div>
  );
};

export default CreateCustomRoleForm;
